














































































































































































import {
  defineComponent,
  ref,
  reactive,
  computed,
  useContext
} from '@nuxtjs/composition-api';
import { useReview, useFooter } from '@vue-storefront/novulo';
import useWindowResize from '~/hooks/useResizeWindow';
import useWriteReviewDrawerOpener from '~/hooks/useWriteReviewDrawerOpener';

export default defineComponent({
  name: 'WriteReviewDrawer',
  props: {
    productId: {
      type: Number,
      default: 0
    },
    productTag: {
      type: Number,
      default: 0
    }
  },
  setup(props) {
    const { isWriteReviewDrawerOpen, toggleWriteReviewDrawer } =
      useWriteReviewDrawerOpener();

    const { writeReviewEssentials, writeReview } = useReview();

    const { i18n } = useContext();

    // social media for thank you message have to be extracted from footer
    const { footerResult } = useFooter();
    const { isDesktop } = useWindowResize();

    const isDone = ref(false);
    const isLoading = ref(false);
    const error = ref(null);

    const form = ref(null);

    const model = reactive({
      recommended: null,
      ratings: {
        solidity: null,
        design: null,
        easeOfUse: null,
        quality: null,
        possibilities: null
      },
      title: '',
      description: '',
      email: '',
      name: ''
    });

    const getElement = (element) => {
      return (
        writeReviewEssentials.value?.headless_containers?.[0]?.elements?.find(
          (item) => item.description.startsWith(element)
        ) || null
      );
    };

    const titleData = computed(() => {
      const productName = writeReviewEssentials.value?.product_name || '';
      const container = getElement('Write review');
      const title =
        container?.custom_fields?.find((d) => d.key === 'Title')
          ?.value_string || '';
      const content =
        container?.custom_fields?.find((d) => d.key === 'Content')
          ?.value_string_area || '';
      const formattedContent = content
        .replace(
          '%product',
          `<span style="font-weight: 700;">${productName}</span>`
        )
        .replaceAll('\n', '<br />');
      return {
        title,
        content: formattedContent
      };
    });

    const thankYouHTML = computed(() => {
      const container = getElement('Confirmation');
      return container?.html_content || '';
    });

    const thankYouSocials = computed(() => {
      const socialsComponents = {
        Facebook: 'IconsSocialFacebook',
        Instagram: 'IconsSocialInstagram',
        Twitter: 'IconsSocialTwitter',
        'You Tube': 'IconsSocialYoutube',
        Linkedin: 'IconsSocialLinkedin',
        Mail: 'IconsSocialMail'
      };

      const unformattedSocials =
        footerResult.value.footer_social_media?.elements?.[0]?.urls || [];

      return unformattedSocials.map((s) => {
        const socialIcon = socialsComponents[s.title] || null;
        return {
          name: s.overlay_text,
          url: s.url,
          icon: socialIcon
        };
      });
    });
    const getItemByEnglishDescription = (
      itemType: 'review_questions' | 'response_options',
      descriptionEN: string
    ) => {
      return (
        writeReviewEssentials.value?.[itemType]?.find?.(
          (item) => item.description_en === descriptionEN
        ) || null
      );
    };

    const getTranslatedRadioValue = (englishValue: string) => {
      return (
        getItemByEnglishDescription('response_options', englishValue)
          ?.description || ''
      );
    };

    const getTranslatedRatingItemName = (item: string) => {
      const itemsApiDescriptionNames = {
        recommended: 'Recommended',
        solidity: 'Robustness',
        design: 'Design',
        easeOfUse: 'Ease of Use',
        quality: 'Quality',
        possibilities: 'Available Options'
      };

      const itemApiName = itemsApiDescriptionNames[item] || null;
      if (!itemApiName) return '';
      const apiItem = getItemByEnglishDescription(
        'review_questions',
        itemApiName
      );
      return apiItem?.description || '';
    };

    const getTranslatedRatingTextValue = (rating: number) => {
      const ratingsEnglishDescriptions = [
        'Poor',
        'Moderate',
        'Adequate',
        'Good',
        'Excellent!'
      ];
      const itemApiName = ratingsEnglishDescriptions[rating - 1] || null;
      if (!itemApiName) return '';
      const apiItem = getItemByEnglishDescription(
        'response_options',
        itemApiName
      );
      return apiItem?.description || '';
    };

    const getTranslatedFieldLabel = (englishLabel: string) => {
      // name and email have a specific way of getting the translated name
      const _getNameOrEmail = (englishLabel: string) => {
        const element = getElement('Name and Email');
        const fieldsMap = {
          Name: 'field_text_1',
          Email: 'field_text_2'
        };
        const translatedText =
          element?.custom_fields?.find((d) => d.key === fieldsMap[englishLabel])
            ?.value_string || '';
        return translatedText;
      };

      if (englishLabel === 'Name' || englishLabel === 'Email')
        return _getNameOrEmail(englishLabel);

      const apiItem = getItemByEnglishDescription(
        'review_questions',
        englishLabel
      );
      return apiItem?.description || '';
    };

    const getPrivacyPolicyLink = () => {
      const element = getElement('Privacy policy');
      const link =
        element?.urls?.find((d) => d.code_english.startsWith('privacy'))?.url ||
        '#';
      return link;
    };

    const clearFormFields = () => {
      model.recommended = null;
      model.ratings = {
        solidity: null,
        design: null,
        easeOfUse: null,
        quality: null,
        possibilities: null
      };
      model.title = '';
      model.description = '';
      model.email = '';
      model.name = '';
    };

    const handleSubmitForm = () => {
      if (form.value) {
        form.value.validate().then((success: boolean) => {
          if (success) {
            isLoading.value = true;
            writeReview({
              productId: props.productId,
              productTag: props.productTag,
              ...model
            })
              .then(() => {
                isLoading.value = false;
                isDone.value = true;
                clearFormFields();
              })
              .catch(() => {
                error.value = i18n.t('generic_error_message');
                isLoading.value = false;
              });
          } else {
            error.value = i18n.t('fill_all_required_fields_message');
          }
        });
      }
    };

    const handleSocialClick = (social: any) => {
      if (!social.url) return;
      window.open(social.url, '_blank');
    };

    const handleCloseDrawer = () => {
      toggleWriteReviewDrawer(false);
    };

    return {
      isWriteReviewDrawerOpen,
      isDone,
      isLoading,
      error,
      isDesktop,
      form,
      model,
      titleData,
      thankYouHTML,
      thankYouSocials,
      getTranslatedRatingItemName,
      getTranslatedRatingTextValue,
      getTranslatedRadioValue,
      getTranslatedFieldLabel,
      getPrivacyPolicyLink,
      handleSubmitForm,
      handleSocialClick,
      handleCloseDrawer
    };
  }
});
